@if (showSidebar()) {
  <!-- Task 5.5: Mobile backdrop overlay (only on workspace pages, not chat pages) -->
  @if (shouldShowBackdrop()) {
    <div
      class="sidebar-backdrop"
      (click)="closeSidebar()"
      (keydown.escape)="closeSidebar()"
      aria-hidden="true"
    ></div>
  }

  <nav
    class="workspace-sidebar"
    [class.expanded]="isExpanded()"
    [class.collapsed]="!isExpanded()"
    [class.mobile]="isMobile()"
    [style.width]="sidebarWidth()"
    [attr.aria-label]="'WORKSPACE_SIDEBAR.NAVIGATION_ARIA_LABEL' | translate"
  >
    <!-- No toggle button on desktop - expansion is route-driven -->
    <!-- Mobile uses hamburger menu button outside sidebar -->

    <div class="sidebar-nav">
      <!-- Workspace home -->
      <button
        class="nav-item"
        [class.active]="activeItem() === 'home'"
        (click)="navigateToHome()"
        (keydown.enter)="navigateToHome()"
        (keydown.space)="navigateToHome(); $event.preventDefault()"
        [matTooltip]="
          isExpanded() ? '' : ('WORKSPACE_SIDEBAR.HOME_TOOLTIP' | translate)
        "
        matTooltipPosition="right"
        [attr.aria-label]="'WORKSPACE_SIDEBAR.HOME_ARIA_LABEL' | translate"
        type="button"
        tabindex="0"
      >
        <mat-icon class="nav-icon" aria-hidden="true">home</mat-icon>
        @if (isExpanded()) {
          <span class="nav-label">{{
            'WORKSPACE_SIDEBAR.HOME_TOOLTIP' | translate
          }}</span>
        }
      </button>

      <button
        class="nav-item"
        [class.active]="activeItem() === 'settings'"
        (click)="navigateToSettings()"
        (keydown.enter)="navigateToSettings()"
        (keydown.space)="navigateToSettings(); $event.preventDefault()"
        [matTooltip]="
          isExpanded() ? '' : ('WORKSPACE_SIDEBAR.SETTINGS_TOOLTIP' | translate)
        "
        matTooltipPosition="right"
        [attr.aria-label]="'WORKSPACE_SIDEBAR.SETTINGS_ARIA_LABEL' | translate"
        type="button"
        tabindex="0"
      >
        <mat-icon class="nav-icon" aria-hidden="true">settings</mat-icon>
        @if (isExpanded()) {
          <span class="nav-label">{{
            'WORKSPACE_SIDEBAR.SETTINGS_TOOLTIP' | translate
          }}</span>
        }
      </button>

      <button
        class="nav-item"
        [class.active]="activeItem() === 'chats'"
        (click)="onViewAllChats()"
        (keydown.enter)="onViewAllChats()"
        (keydown.space)="onViewAllChats(); $event.preventDefault()"
        [matTooltip]="
          isExpanded()
            ? ''
            : ('WORKSPACE.RECENT_CHATS.SIDEBAR.TITLE' | translate)
        "
        matTooltipPosition="right"
        [attr.aria-label]="'WORKSPACE.RECENT_CHATS.SIDEBAR.TITLE' | translate"
        type="button"
        tabindex="0"
      >
        <mat-icon class="nav-icon" aria-hidden="true">history</mat-icon>
        @if (isExpanded()) {
          <span class="nav-label">{{
            'WORKSPACE.RECENT_CHATS.SIDEBAR.TITLE' | translate
          }}</span>
        }
      </button>

      <!-- Recent chats list -->
      @if (isExpanded()) {
        <div
          class="recent-chats-list"
          [attr.aria-label]="
            'WORKSPACE_SIDEBAR.RECENT_CHATS_LIST_ARIA_LABEL' | translate
          "
        >
          @if (showSidebarSearchInput()) {
            <!-- Search/Filter Input -->
            <div class="search-container">
              <div class="search-wrapper">
                <mat-icon class="search-icon">search</mat-icon>
                <input
                  type="text"
                  class="search-input"
                  [ngModel]="searchQuery()"
                  (ngModelChange)="onSidebarSearchChange($event)"
                  [placeholder]="
                    'WORKSPACE.RECENT_CHATS.SEARCH.PLACEHOLDER' | translate
                  "
                  [attr.aria-label]="
                    'WORKSPACE.RECENT_CHATS.SEARCH.PLACEHOLDER' | translate
                  "
                />
                @if (searchQuery()) {
                  <button
                    type="button"
                    class="clear-search-btn"
                    (click)="clearSearch()"
                    [attr.aria-label]="'COMMON.CLEAR' | translate"
                  >
                    <app-icon name="close" [size]="16" />
                  </button>
                }
              </div>
            </div>
          }

          <!-- Task 4.6: Loading state -->
          @if (recentChatsLoading()) {
            <output class="recent-chats-loading" aria-live="polite">
              <mat-spinner
                diameter="24"
                [attr.aria-label]="
                  'WORKSPACE_SIDEBAR.LOADING_RECENT_CHATS_ARIA_LABEL'
                    | translate
                "
              ></mat-spinner>
              <span class="loading-text">{{
                'CHAT.LOADING_SESSIONS' | translate
              }}</span>
            </output>
          } @else if (recentChatsError()) {
            <div class="recent-chats-error" role="alert" aria-live="assertive">
              <mat-icon class="error-icon" aria-hidden="true"
                >error_outline</mat-icon
              >
              <span class="error-text">{{
                'WORKSPACE_SIDEBAR.RECENT_CHATS_LOAD_FAILED' | translate
              }}</span>
            </div>
          } @else if (searchQuery() && filteredRecentChats().length === 0) {
            <div class="empty-state">
              <mat-icon>search_off</mat-icon>
              <p>
                {{ 'WORKSPACE.RECENT_CHATS.SEARCH.NO_RESULTS' | translate }}
              </p>
              <button mat-stroked-button (click)="clearSearch()">
                {{ 'COMMON.CLEAR_SEARCH' | translate }}
              </button>
            </div>
          } @else if (recentChats().length === 0) {
            <div class="empty-state">
              <mat-icon>chat_bubble</mat-icon>
              <p>{{ 'WORKSPACE.RECENT_CHATS.EMPTY.HEADING' | translate }}</p>
            </div>
          } @else {
            <ul class="recent-chat-items">
              @for (group of chatGroupEntries(); track group.key) {
                @if (group.items.length > 0) {
                  <li class="chat-group">
                    <div class="group-header">
                      {{ group.labelKey | translate }}
                    </div>
                    <ul class="chat-group-items">
                      @for (chat of group.items; track chat.sessionId) {
                        <li>
                          <app-workspace-recent-chat-item
                            [chat]="chat"
                            [showTimestamp]="false"
                            (chatClick)="navigateToChat($event)"
                            (chatRename)="onChatRename($event)"
                            (chatDelete)="onChatDelete($event)"
                          />
                        </li>
                      }
                    </ul>
                  </li>
                }
              }

              <li class="recent-chat-actions">
                <button
                  type="button"
                  class="view-more-button"
                  (click)="onViewAllChats()"
                  [attr.aria-label]="
                    'WORKSPACE.RECENT_CHATS.SIDEBAR.VIEW_ALL' | translate
                  "
                >
                  {{ 'WORKSPACE.RECENT_CHATS.SIDEBAR.VIEW_ALL' | translate }}
                </button>
              </li>
            </ul>
          }
        </div>
      }
    </div>
  </nav>
}
